import sumBy from 'lodash/sumBy';
import { atom, AtomEffect, DefaultValue, selector } from 'recoil';
import isEqual from 'lodash/isEqual';
import { CheckoutStore, Facility, Space, RentalCartItem } from 'types/checkout';
import { localStorage } from 'lib/storage';
import { SelectedAddonInterface } from 'types/addon';
import { Product, ProductPackage } from 'types/product';
import { ResourcesToProduct } from 'types/resources-to-products';
import { Season } from 'types/seasons';
import { Program } from 'types/program';
import { PaginatedResourceResponse } from '../types/resources';

const CHECKOUT_STORAGE_KEY = 'checkoutState';

export interface RentalCheckoutStore extends CheckoutStore {
	// The packages the user selected in the landing page Search module
	selectedPackages?: RentalCartItem[];
	// facility information
	currentFacility?: Facility;
	// space information
	currentSpace?: Space;
	// selected sport
	currentSportActivity?: number;
	// space type
	spaceTypeSelection?: string;
	// for search
	selectedDate?: string;
	// request payment
	requestOnly?: boolean;
	/* eslint-disable @typescript-eslint/no-explicit-any*/
	selectedAnswersForInit?: any;

	// for advanced search
	recurring?: boolean;
	repeatPeriod?: string;
	numberOfOccurrences?: number;

	isAll?: boolean;
	selectedProduct?: Product;
	selectedResources?: ResourcesToProduct;
	stepsCompletedOtherProducts?: any[];
	selectedSeason?: Season;
	questionnaires?: number[];
	selectedProgram?: Program;
	productResources?: PaginatedResourceResponse;
	selectedProductPackage?: ProductPackage;
	isWaitlistRegistration?: boolean;
}

const localStorageEffect =
	(key: string): AtomEffect<RentalCheckoutStore> =>
	({ setSelf, onSet }) => {
		const savedValue = localStorage.getItem(key);

		if (savedValue !== null) {
			setSelf(savedValue);
		}

		onSet(newValue => {
			if (newValue instanceof DefaultValue) {
				localStorage.removeItem(key);
			}

			localStorage.setItem(key, newValue);
		});
	};

const checkout = atom<RentalCheckoutStore>({
	key: 'rentalCheckout',
	default: { currentFacility: { facilityId: 0, organizationId: 0 } },
	// Based on:
	// https://recoiljs.org/docs/guides/atom-effects/#local-storage-persistence
	effects_UNSTABLE: [localStorageEffect(CHECKOUT_STORAGE_KEY)],
});

const checkoutCartItems = selector({
	key: 'rentalCheckoutCartItems',
	get: ({ get }) => {
		let cartItems: Array<unknown | RentalCartItem | SelectedAddonInterface> = [];
		const checkoutState = get(checkout);

		checkoutState?.selectedPackages?.forEach((selectedPackage: RentalCartItem) => {
			cartItems = [...cartItems, selectedPackage];
			const addons = checkoutState?.selectedAddons?.filter((selectedAddon: SelectedAddonInterface) =>
				isEqual(selectedAddon.package, selectedPackage)
			);

			if (addons?.length) {
				cartItems = [...cartItems, ...addons];
			}
		});

		return cartItems;
	},
});

const checkoutCartBookingFee = selector<number>({
	key: 'rentalCheckoutCartBookingFee',
	get: () => {
		return 0;
	},
});

const checkoutCartTotalPrice = selector<number>({
	key: 'rentalCheckoutCartTotalPrice',
	get: ({ get }) => {
		const checkoutCartItemsState = get(checkoutCartItems);

		return sumBy(checkoutCartItemsState, 'price');
	},
});

const checkoutEventApplications = selector({
	key: 'localRentalCheckoutEventApplications',
	get: ({ get }) => {
		const checkoutState = get(checkout);

		return checkoutState?.selectedAnswersForInit ?? {};
	},
});

export const checkoutStore = {
	checkout,
	checkoutCartItems,
	checkoutCartBookingFee,
	checkoutCartTotalPrice,
	checkoutEventApplications,
};
